import { isObject } from 'lodash-es';

import type { EndpointRequest } from '@iconik-io/iconik-api/src/client/request';
import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';

import type { SelectOption } from './types';

export type LoadOptionsResult<O = SelectOption> =
  | O[]
  | {
      options: O[];
      incomplete?: boolean;
    };

/**
 * A multi select reports bare ids with `plainMultiValues` and whole options
 * without it, so a handler that wants one shape says which.
 */
export const selectedIds = (values: (string | SelectOption)[]): string[] =>
  values.map((value) => (typeof value === 'string' ? value : value.value));

/** See {@link selectedIds}. */
export const selectedOptions = (
  values: (string | SelectOption)[],
): SelectOption[] =>
  values.map((value) =>
    typeof value === 'string' ? { label: value, value } : value,
  );

export const explodeLoadOptionsResult = <O>(
  result: LoadOptionsResult<O>,
): [options: O[], incomplete: boolean] =>
  Array.isArray(result)
    ? [result, false]
    : [result.options, result.incomplete ?? false];

/**
 * Preprocess remote response to handle incomplete options data and
 * create LoadOptionsResult from it.
 *
 * @param responseToOptions
 */
export const resolveRemoteResponse =
  <Request extends EndpointRequest, Option>(
    responseToOptions: (response: EndpointResponse<Request>) => Option[],
  ) =>
  (response: EndpointResponse<Request>): LoadOptionsResult<Option> => {
    const body = response.body;
    const incomplete =
      isObject(body) && 'pages' in body && body.pages ? body.pages > 1 : false;
    return {
      options: responseToOptions(response),
      incomplete,
    };
  };
